import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tags, Loader2, Eye, Check, RotateCcw, FileSpreadsheet, Percent, TrendingDown, Package, DollarSign, Store } from "lucide-react";
import { API_URL as API } from "@/lib/api";

const MODOS = [
  { value: "fijo", label: "Descuento fijo (%)" },
  { value: "antiguedad", label: "Escalonado por antiguedad de lote" },
  { value: "volumen", label: "Escalonado por volumen de stock" },
  { value: "mixto", label: "Mixto (mayor entre antiguedad y volumen)" },
];

const fUSD = (v) => "$ " + new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const fBs = (v) => new Intl.NumberFormat("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0) + " Bs";

const Kpi = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <Card className={`p-3 border border-border border-l-4 ${accent}`} data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground body-font mb-1">{label}</p>
        <p className="mono-font text-lg font-bold text-foreground truncate">{value}</p>
        {sub && <p className="mono-font text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
    </div>
  </Card>
);

const Liquidacion = ({ globalTC }) => {
  const [filters, setFilters] = useState({
    modo: "fijo",
    descuento_pct: 20,
    descuento_max: 70,
    search: "",
    familia: "",
    subfamilia: "",
    lote_desde: "",
    lote_hasta: "",
    stock_min: "",
    stock_max: "",
  });
  const [escAnt, setEscAnt] = useState([
    { anios: 0, pct: 10 },
    { anios: 1, pct: 25 },
    { anios: 2, pct: 40 },
    { anios: 3, pct: 55 },
  ]);
  const [escVol, setEscVol] = useState([
    { stock_min: 20, pct: 15 },
    { stock_min: 50, pct: 25 },
    { stock_min: 100, pct: 35 },
  ]);
  const [opciones, setOpciones] = useState({ familias: [], subfamilias: [], lotes: [] });
  const [subfamOpts, setSubfamOpts] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [codigosManuales, setCodigosManuales] = useState([]);
  const [preview, setPreview] = useState(null);
  const [activos, setActivos] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("preview");

  const buildBody = useCallback((overrides = {}) => ({
    modo: filters.modo,
    descuento_pct: Number(filters.descuento_pct) || 0,
    descuento_max: Number(filters.descuento_max) || 70,
    escalones_antiguedad: escAnt,
    escalones_volumen: escVol,
    search: filters.search || null,
    familia: filters.familia || null,
    subfamilia: filters.subfamilia || null,
    lote_desde: filters.lote_desde || null,
    lote_hasta: filters.lote_hasta || null,
    stock_min: filters.stock_min !== "" ? Number(filters.stock_min) : null,
    stock_max: filters.stock_max !== "" ? Number(filters.stock_max) : null,
    solo_stock_positivo: true,
    codigos: codigosManuales.length ? codigosManuales : null,
    ...overrides,
  }), [filters, escAnt, escVol, codigosManuales]);

  const fetchOpciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/filters`);
      const lotes = (res.data.lotes || [])
        .filter((l) => /^\d+$/.test(String(l)))
        .sort((a, b) => Number(a) - Number(b));
      setOpciones({ familias: res.data.familias || [], subfamilias: res.data.subfamilias || [], lotes });
      setSubfamOpts(res.data.subfamilias || []);
    } catch (err) {
      console.error("Error cargando filtros", err);
    }
  }, []);

  const onFamiliaChange = async (familia) => {
    setFilters((prev) => ({ ...prev, familia, subfamilia: "" }));
    if (!familia) return setSubfamOpts(opciones.subfamilias);
    try {
      const res = await axios.get(`${API}/api/subfamilias/${encodeURIComponent(familia)}`);
      setSubfamOpts(res.data.subfamilias || []);
    } catch (err) {
      setSubfamOpts([]);
    }
  };

  const buscarSugerencias = async (q) => {
    setF("search", q);
    if (!q || q.length < 2) return setSugerencias([]);
    setBuscando(true);
    try {
      const res = await axios.get(`${API}/api/products/search-lite`, { params: { q, limit: 20 } });
      setSugerencias(res.data.items || []);
    } catch (err) {
      setSugerencias([]);
    } finally {
      setBuscando(false);
    }
  };

  const toggleCodigoManual = (codigo) => {
    setCodigosManuales((prev) => prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]);
  };

  const fetchActivos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/liquidacion/activos`);
      setActivos(res.data);
    } catch (err) {
      console.error("Error cargando liquidaciones activas", err);
    }
  }, []);

  useEffect(() => { fetchOpciones(); fetchActivos(); }, [fetchOpciones, fetchActivos]);

  const runPreview = async () => {
    setLoading(true);
    setSugerencias([]);
    try {
      const res = await axios.post(`${API}/api/liquidacion/preview`, buildBody());
      setPreview(res.data);
      setSelected(res.data.items.map((i) => i.codigo));
      setView("preview");
      if (res.data.items.length === 0) {
        toast.info(
          res.data.candidatos === 0
            ? "Ningun producto coincide con los filtros (revisa familia/subfamilia y el rango de lote)"
            : `${res.data.candidatos} productos coinciden, pero la regla les asigna 0% de descuento`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error generando vista previa");
    } finally {
      setLoading(false);
    }
  };

  const aplicar = async () => {
    if (!selected.length) return toast.error("Selecciona al menos un producto");
    if (!window.confirm(`Aplicar liquidacion a ${selected.length} productos? El precio base NO se modifica.`)) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/liquidacion/aplicar`, buildBody({ codigos: selected }));
      toast.success(res.data.message);
      await fetchActivos();
      setView("activos");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error aplicando liquidacion");
    } finally {
      setLoading(false);
    }
  };

  const revertir = async (codigos) => {
    if (!window.confirm(codigos?.length ? `Revertir ${codigos.length} productos?` : "Revertir TODAS las liquidaciones activas?")) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/liquidacion/revertir`, { codigos: codigos || null });
      toast.success(res.data.message);
      await fetchActivos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error revirtiendo");
    } finally {
      setLoading(false);
    }
  };

  const consolidar = async (codigos) => {
    if (!window.confirm("Consolidar hara el precio de liquidacion DEFINITIVO (reemplaza el precio base). Continuar?")) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/liquidacion/consolidar`, { codigos: codigos || null });
      toast.success(res.data.message);
      await fetchActivos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error consolidando");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async (soloActivos) => {
    try {
      const body = soloActivos ? buildBody({ modo: "activos" }) : buildBody();
      const res = await axios.post(`${API}/api/liquidacion/export-excel`, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `liquidacion_${soloActivos ? "activos" : "preview"}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (err) {
      toast.error("Error exportando Excel");
    }
  };

  const data = view === "activos" ? activos : preview;
  const items = data?.items || [];
  const resumen = data?.resumen;
  const tc = data?.tc || globalTC || 0;

  const allSelected = useMemo(() => items.length > 0 && items.every((i) => selected.includes(i.codigo)), [items, selected]);
  const toggleAll = () => setSelected(allSelected ? [] : items.map((i) => i.codigo));
  const toggleOne = (codigo) => setSelected((prev) => prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]);

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-4" data-testid="liquidacion-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-font text-2xl font-bold text-foreground flex items-center gap-2">
            <Tags className="h-6 w-6 text-[#FF9900]" /> Liquidacion de Inventario
          </h1>
          <p className="body-font text-sm text-muted-foreground">
            Descuentos masivos por lote, familia, subfamilia o volumen. El precio base se conserva hasta consolidar. TC: {tc}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === "preview" ? "default" : "outline"}
            size="sm"
            onClick={() => setView("preview")}
            className={view === "preview" ? "bg-[#232F3E] hover:bg-[#232F3E]/90 text-white text-xs" : "text-xs"}
            data-testid="view-preview-btn"
          >
            Simulador
          </Button>
          <Button
            variant={view === "activos" ? "default" : "outline"}
            size="sm"
            onClick={() => { setView("activos"); fetchActivos(); }}
            className={view === "activos" ? "bg-[#232F3E] hover:bg-[#232F3E]/90 text-white text-xs" : "text-xs"}
            data-testid="view-activos-btn"
          >
            En liquidacion ({activos?.resumen?.count || 0})
          </Button>
        </div>
      </div>

      {view === "preview" && (
        <Card className="p-4 border border-border space-y-4" data-testid="liquidacion-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground body-font">Regla de descuento</label>
              <Select value={filters.modo} onValueChange={(v) => setF("modo", v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="liq-modo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODOS.map((m) => <SelectItem key={m.value} value={m.value} className="text-xs">{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground body-font">% Descuento fijo</label>
              <Input
                type="number" min="0" max="100" value={filters.descuento_pct}
                onChange={(e) => setF("descuento_pct", e.target.value)}
                disabled={filters.modo !== "fijo"}
                className="h-9 text-xs mono-font" data-testid="liq-descuento-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground body-font">% Tope maximo</label>
              <Input type="number" min="0" max="100" value={filters.descuento_max}
                onChange={(e) => setF("descuento_max", e.target.value)}
                className="h-9 text-xs mono-font" data-testid="liq-descuento-max-input" />
            </div>
            <div className="relative">
              <label className="text-[10px] uppercase text-muted-foreground body-font">Buscar y marcar productos</label>
              <Input value={filters.search} onChange={(e) => buscarSugerencias(e.target.value)}
                placeholder="Ej: cable" className="h-9 text-xs" data-testid="liq-search-input" />
              {sugerencias.length > 0 && (
                <div className="absolute z-40 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="liq-search-suggestions">
                  {sugerencias.map((s, i) => (
                    <button
                      key={s.codigo}
                      onClick={() => toggleCodigoManual(s.codigo)}
                      className="flex items-start gap-2 w-full text-left px-2 py-1.5 hover:bg-muted transition-colors border-b border-border/40 last:border-0"
                      data-testid={`liq-suggestion-${i}`}
                    >
                      <span className={`mt-0.5 h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center ${codigosManuales.includes(s.codigo) ? 'bg-[#FF9900] border-[#FF9900]' : 'border-border'}`}>
                        {codigosManuales.includes(s.codigo) && <Check className="h-2.5 w-2.5 text-black" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block mono-font text-[10px] text-foreground">{s.codigo}</span>
                        <span className="block body-font text-[10px] text-muted-foreground line-clamp-1">{s.descripcion}</span>
                        <span className="block mono-font text-[10px] text-muted-foreground">Lote {s.lote} · Stock {s.stock} · {fUSD(s.precio_usd)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {buscando && <span className="absolute right-2 top-7"><Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /></span>}
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground body-font">Familia</label>
              <Select value={filters.familia || "__all__"} onValueChange={(v) => onFamiliaChange(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="liq-familia-select"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__all__" className="text-xs">Todas</SelectItem>
                  {opciones.familias.map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground body-font">SubFamilia</label>
              <Select value={filters.subfamilia || "__all__"} onValueChange={(v) => setF("subfamilia", v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="liq-subfamilia-select"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__all__" className="text-xs">Todas</SelectItem>
                  {subfamOpts.map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase text-muted-foreground body-font">Lote desde</label>
                <Select value={filters.lote_desde || "__all__"} onValueChange={(v) => setF("lote_desde", v === "__all__" ? "" : v)}>
                  <SelectTrigger className="h-9 text-xs mono-font" data-testid="liq-lote-desde-select"><SelectValue placeholder="Min" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__" className="text-xs">Todos</SelectItem>
                    {opciones.lotes.map((l) => <SelectItem key={l} value={String(l)} className="text-xs mono-font">{l} ({2000 + Math.floor(Number(l) / 10)})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-muted-foreground body-font">Lote hasta</label>
                <Select value={filters.lote_hasta || "__all__"} onValueChange={(v) => setF("lote_hasta", v === "__all__" ? "" : v)}>
                  <SelectTrigger className="h-9 text-xs mono-font" data-testid="liq-lote-hasta-select"><SelectValue placeholder="Max" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__" className="text-xs">Todos</SelectItem>
                    {opciones.lotes.map((l) => <SelectItem key={l} value={String(l)} className="text-xs mono-font">{l} ({2000 + Math.floor(Number(l) / 10)})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase text-muted-foreground body-font">Stock min</label>
                <Input type="number" value={filters.stock_min} onChange={(e) => setF("stock_min", e.target.value)}
                  className="h-9 text-xs mono-font" data-testid="liq-stock-min-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-muted-foreground body-font">Stock max</label>
                <Input type="number" value={filters.stock_max} onChange={(e) => setF("stock_max", e.target.value)}
                  className="h-9 text-xs mono-font" data-testid="liq-stock-max-input" />
              </div>
            </div>
          </div>

          {(filters.modo === "antiguedad" || filters.modo === "mixto") && (
            <div className="border-t border-border pt-3" data-testid="liq-escalones-antiguedad">
              <p className="text-xs font-semibold text-foreground body-font mb-2">Escalones por antiguedad de lote (años)</p>
              <div className="flex gap-3 flex-wrap">
                {escAnt.map((e, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 bg-muted/40 rounded-md px-2 py-1.5">
                    <span className="text-[10px] text-muted-foreground body-font">≥ {e.anios} año(s)</span>
                    <Input type="number" value={e.pct} onChange={(ev) => {
                      const v = Number(ev.target.value);
                      setEscAnt((prev) => prev.map((x, i) => i === idx ? { ...x, pct: v } : x));
                    }} className="h-7 w-16 text-xs mono-font" data-testid={`liq-esc-ant-${idx}`} />
                    <Percent className="h-3 w-3 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(filters.modo === "volumen" || filters.modo === "mixto") && (
            <div className="border-t border-border pt-3" data-testid="liq-escalones-volumen">
              <p className="text-xs font-semibold text-foreground body-font mb-2">Escalones por volumen de stock remanente</p>
              <div className="flex gap-3 flex-wrap">
                {escVol.map((e, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 bg-muted/40 rounded-md px-2 py-1.5">
                    <span className="text-[10px] text-muted-foreground body-font">stock ≥</span>
                    <Input type="number" value={e.stock_min} onChange={(ev) => {
                      const v = Number(ev.target.value);
                      setEscVol((prev) => prev.map((x, i) => i === idx ? { ...x, stock_min: v } : x));
                    }} className="h-7 w-16 text-xs mono-font" data-testid={`liq-esc-vol-stock-${idx}`} />
                    <Input type="number" value={e.pct} onChange={(ev) => {
                      const v = Number(ev.target.value);
                      setEscVol((prev) => prev.map((x, i) => i === idx ? { ...x, pct: v } : x));
                    }} className="h-7 w-16 text-xs mono-font" data-testid={`liq-esc-vol-pct-${idx}`} />
                    <Percent className="h-3 w-3 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {codigosManuales.length > 0 && (
            <div className="border-t border-border pt-3" data-testid="liq-codigos-manuales">
              <p className="text-xs font-semibold text-foreground body-font mb-2">
                Productos marcados ({codigosManuales.length}) — los filtros se aplican solo a estos
              </p>
              <div className="flex gap-1.5 flex-wrap">
                {codigosManuales.map((c) => (
                  <button key={c} onClick={() => toggleCodigoManual(c)}
                    className="mono-font text-[10px] px-2 py-1 rounded-full bg-[#FF9900]/15 text-[#FF9900] border border-[#FF9900]/40 hover:bg-[#FF9900]/25">
                    {c} ✕
                  </button>
                ))}
                <button onClick={() => setCodigosManuales([])} className="body-font text-[10px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:bg-muted" data-testid="liq-clear-codigos">
                  Limpiar seleccion
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap border-t border-border pt-3">
            <Button onClick={runPreview} disabled={loading} className="bg-[#FF9900] hover:bg-[#e88b00] text-black text-xs font-semibold" data-testid="liq-preview-btn">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
              Vista previa
            </Button>
            <Button onClick={aplicar} disabled={loading || !preview?.items?.length} variant="outline" className="text-xs" data-testid="liq-aplicar-btn">
              <Check className="h-3.5 w-3.5 mr-1.5" /> Aplicar a seleccionados ({selected.length})
            </Button>
            <Button onClick={() => exportExcel(false)} disabled={!preview?.items?.length} variant="outline" className="text-xs" data-testid="liq-export-preview-btn">
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Exportar simulacion
            </Button>
          </div>
        </Card>
      )}

      {view === "activos" && (
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => consolidar(selected.length ? selected : null)} disabled={loading || !items.length} className="bg-[#232F3E] hover:bg-[#232F3E]/90 text-white text-xs" data-testid="liq-consolidar-btn">
            <Check className="h-3.5 w-3.5 mr-1.5" /> Consolidar {selected.length ? `(${selected.length})` : "todos"}
          </Button>
          <Button onClick={() => revertir(selected.length ? selected : null)} disabled={loading || !items.length} variant="outline" className="text-xs text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20" data-testid="liq-revertir-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Revertir {selected.length ? `(${selected.length})` : "todos"}
          </Button>
          <Button onClick={() => exportExcel(true)} disabled={!items.length} variant="outline" className="text-xs" data-testid="liq-export-activos-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Exportar liquidacion activa
          </Button>
        </div>
      )}

      {resumen && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="liq-resumen">
          <Kpi label="Productos" value={resumen.count} sub={`${resumen.unidades} u. almacen`} icon={Package} accent="border-l-[#232F3E] dark:border-l-[#FF9900]" testId="liq-kpi-count" />
          <Kpi
            label="En locales"
            value={resumen.items_en_locales || 0}
            sub={`MK ${resumen.unidades_manco_kapac || 0} u. · PB ${resumen.unidades_plaza_bonita || 0} u.`}
            icon={Store}
            accent="border-l-violet-500"
            testId="liq-kpi-locales"
          />
          <Kpi label="Dscto promedio" value={`${resumen.descuento_promedio}%`} icon={Percent} accent="border-l-[#FF9900]" testId="liq-kpi-dscto" />
          <Kpi label="Valor actual" value={fUSD(resumen.valor_actual_usd)} sub={fBs(resumen.valor_actual_usd * tc)} icon={DollarSign} accent="border-l-emerald-500" testId="liq-kpi-actual" />
          <Kpi label="Valor liquidacion" value={fUSD(resumen.valor_liquidacion_usd)} sub={fBs(resumen.valor_liquidacion_usd * tc)} icon={Tags} accent="border-l-sky-500" testId="liq-kpi-liquidacion" />
          <Kpi label="Impacto (menor margen)" value={fUSD(resumen.impacto_usd)} sub={fBs(resumen.impacto_usd * tc)} icon={TrendingDown} accent="border-l-red-500" testId="liq-kpi-impacto" />
        </div>
      )}

      <Card className="border border-border overflow-hidden" data-testid="liq-table-card">
        <div className="overflow-x-auto custom-scrollbar max-h-[60vh]">
          <Table>
            <TableHeader>
              <TableRow className="border-0">
                <TableHead className="table-header-amazon text-white text-xs w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="liq-select-all" className="accent-[#FF9900]" />
                </TableHead>
                <TableHead className="table-header-amazon text-white text-xs">Codigo</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">Descripcion</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">Familia</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">Lote</TableHead>
                <TableHead className="table-header-amazon text-white text-xs text-center">Stock Alm.</TableHead>
                <TableHead className="table-header-amazon text-white text-xs text-center">M.Kapac</TableHead>
                <TableHead className="table-header-amazon text-white text-xs text-center">P.Bonita</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">P.USD</TableHead>
                <TableHead className="table-header-amazon text-white text-xs text-center">Dscto</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">P.USD Liq.</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">PMenor Liq.</TableHead>
                <TableHead className="table-header-amazon text-white text-xs">Impacto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-10 body-font text-sm text-muted-foreground">
                    {view === "activos" ? "No hay productos en liquidacion" : "Genera una vista previa para simular descuentos"}
                  </TableCell>
                </TableRow>
              ) : items.map((it, idx) => (
                <TableRow key={it.codigo} className="border-b border-border/50 hover:bg-[#FF9900]/5" data-testid={`liq-row-${idx}`}>
                  <TableCell>
                    <input type="checkbox" checked={selected.includes(it.codigo)} onChange={() => toggleOne(it.codigo)} className="accent-[#FF9900]" data-testid={`liq-check-${idx}`} />
                  </TableCell>
                  <TableCell className="mono-font text-xs font-medium">{it.codigo}</TableCell>
                  <TableCell className="body-font text-xs"><span className="block max-w-[240px] line-clamp-2">{it.descripcion}</span></TableCell>
                  <TableCell className="body-font text-xs text-muted-foreground">{it.familia}</TableCell>
                  <TableCell className="mono-font text-xs">{it.lote} <span className="text-[10px] text-muted-foreground">({it.lote_year || "?"})</span></TableCell>
                  <TableCell className="mono-font text-xs text-center font-bold">{it.stock}</TableCell>
                  <TableCell className="mono-font text-xs text-center text-amber-600 dark:text-amber-400" data-testid={`liq-stock-mk-${idx}`}>{it.stock_manco_kapac || "-"}</TableCell>
                  <TableCell className="mono-font text-xs text-center text-violet-600 dark:text-violet-400" data-testid={`liq-stock-pb-${idx}`}>{it.stock_plaza_bonita || "-"}</TableCell>
                  <TableCell className="mono-font text-xs text-muted-foreground line-through">{fUSD(it.precio_usd)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-[#FF9900]/15 text-[#FF9900] border border-[#FF9900]/40 mono-font text-[10px]" data-testid={`liq-dscto-${idx}`}>-{it.descuento_pct}%</Badge>
                  </TableCell>
                  <TableCell className="mono-font text-xs font-bold text-emerald-600 dark:text-emerald-400" data-testid={`liq-precio-${idx}`}>{fUSD(it.precio_liquidacion_usd)}</TableCell>
                  <TableCell className="mono-font text-xs">{fBs(it.pmenor_liq)}</TableCell>
                  <TableCell className="mono-font text-xs text-red-600 dark:text-red-400">{fUSD(it.impacto_usd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default Liquidacion;
